/**
 * Despesas que ainda não chegaram ao servidor.
 *
 * Sem isto, registrar uma despesa offline a fazia sumir: a lista vem do
 * servidor, e o servidor ainda não sabe dela. A pessoa veria o toast "salva" e
 * uma lista sem a despesa — o pior par possível, porque parece perda de dado.
 *
 * A fila é a fonte: nada é duplicado, porque a mutação sai do outbox no
 * instante em que o servidor confirma, e a partir daí a linha vem de lá.
 */

import type { Mutation } from '@nv/offline';

import { localDb } from '../roteiro/persistence';
import type { ExpenseRow } from './api';

export interface DespesaPendente extends ExpenseRow {
  pendente: true;
  /** `true` quando a mutação está em conflito, esperando decisão. */
  emConflito: boolean;
}

interface PayloadDeDespesa {
  create?: {
    id: string;
    description?: string;
    category?: string;
    scope?: 'trip' | 'private';
    subtotalMinor?: number;
    taxMinor?: number;
    tipMinor?: number;
    currency?: string;
    splitMode?: string;
    spentAt?: string;
  };
}

export function pendentesDaFila(
  mutacoes: readonly Mutation[],
  meuUserId: string,
): DespesaPendente[] {
  return mutacoes
    .filter((m) => m.entity === 'expense' && m.operation === 'create')
    .map((m) => {
      const criada = (m.payload as PayloadDeDespesa).create;
      if (!criada) return null;

      const subtotal = criada.subtotalMinor ?? 0;
      return {
        id: criada.id,
        description: criada.description ?? 'Despesa',
        category: criada.category ?? 'outros',
        scope: criada.scope ?? 'trip',
        totalMinor: subtotal + (criada.taxMinor ?? 0) + (criada.tipMinor ?? 0),
        currency: criada.currency ?? 'BRL',
        splitMode: criada.splitMode ?? 'equal',
        spentAt: criada.spentAt ?? new Date(m.createdAt).toISOString(),
        createdBy: meuUserId,
        version: 0,
        pendente: true as const,
        emConflito: m.status === 'conflict',
      };
    })
    .filter((d): d is DespesaPendente => d !== null);
}

export async function lerPendentes(meuUserId: string): Promise<DespesaPendente[]> {
  const db = await localDb();
  return pendentesDaFila(await db.outbox.list(), meuUserId);
}
